import type { DevelopmentSummaryModel } from '@/domain/models/development.js';
import type { DataTableColumn } from '@/presentation/components/index.js';
import { DevelopmentListStatusCell } from '@/presentation/pages/developments/components/development-list-status-cell.js';
import { JiraEpicStatus } from '@/presentation/pages/developments/development-detail/components/jira-epic-status.js';
import { jiraRetryLabel } from '@/presentation/pages/developments/utils/jira-sync-pending.js';
import { type TranslateFn, formatDateShort } from '@/shared/utils/format-date.js';
import type { Complexity } from '@/shared/utils/format-status.js';

const COMPLEXITY_RANK: Record<Complexity, number> = { MUITO_BAIXA: 1, BAIXA: 2, MEDIA: 3, ALTA: 4, MUITO_ALTA: 5 };

function renderComplexity(complexity: Complexity | null | undefined, translate: (key: string) => string) {
    return complexity ? translate(`complexityLabels.${complexity}`) : '—';
}

function buildIdentifierColumns(translate: TranslateFn): DataTableColumn<DevelopmentSummaryModel>[] {
    return [
        {
            key: 'displayId',
            header: translate('devList.colId'),
            width: 100,
            sortValue: (d) => d.displayId
        },
        {
            key: 'jiraEpicKey',
            header: translate('devList.colJiraEpicKey'),
            width: 140,
            sortValue: (d) => d.jiraEpicKey ?? '',
            render: (d) => (
                <JiraEpicStatus
                    status={d.jiraEpicSync.status}
                    epicKey={d.jiraEpicKey}
                    epicUrl={d.jiraEpicUrl}
                    epicStatusName={null}
                    creatingLabel={translate('devDetail.jiraEpicCreating')}
                    retryLabel={jiraRetryLabel(translate, d.jiraEpicSync.nextAttemptAt)}
                    variant="field"
                    testId={`jira-epic-status-${d.id}`}
                />
            )
        },
        {
            key: 'title',
            header: translate('devList.colTitle'),
            sortValue: (d) => d.title
        },
        {
            key: 'status',
            header: translate('devList.colStatus'),
            sortValue: (d) => d.status,
            render: (d) => <DevelopmentListStatusCell status={d.status} aiAnalysisInProgressLabel={translate('devList.aiAnalysisInProgress')} />
        }
    ];
}

function buildContextColumns(translate: (key: string) => string): DataTableColumn<DevelopmentSummaryModel>[] {
    return [
        {
            key: 'businessProcess',
            header: translate('devList.colBusinessProcess'),
            sortValue: (d) => d.businessProcess ?? '',
            render: (d) => d.businessProcess ?? '—'
        },
        {
            key: 'consultor',
            header: translate('devList.colConsultor'),
            sortValue: (d) => d.consultor?.name ?? '',
            render: (d) => d.consultor?.name ?? '—'
        },
        {
            key: 'keyUser',
            header: translate('devList.colKeyUser'),
            sortValue: (d) => d.keyUser?.name ?? '',
            render: (d) => d.keyUser?.name ?? '—'
        },
        {
            key: 'createdAt',
            header: translate('devList.colCreatedAt'),
            sortValue: (d) => d.createdAt,
            render: (d) => formatDateShort(d.createdAt)
        }
    ];
}

function buildComplexityColumns(translate: (key: string) => string): DataTableColumn<DevelopmentSummaryModel>[] {
    return [
        {
            key: 'functionalComplexity',
            header: translate('devList.colFunctionalComplexity'),
            sortValue: (d) => (d.functionalComplexity ? COMPLEXITY_RANK[d.functionalComplexity] : 0),
            render: (d) => renderComplexity(d.functionalComplexity, translate)
        },
        {
            key: 'techComplexity',
            header: translate('devList.colTechComplexity'),
            sortValue: (d) => (d.techComplexity ? COMPLEXITY_RANK[d.techComplexity] : 0),
            render: (d) => renderComplexity(d.techComplexity, translate)
        }
    ];
}

function buildDateColumns(translate: (key: string) => string): DataTableColumn<DevelopmentSummaryModel>[] {
    return [
        {
            key: 'efValidatedAt',
            header: translate('devList.colEfValidatedAt'),
            sortValue: (d) => d.efValidatedAt ?? '',
            render: (d) => (d.efValidatedAt ? formatDateShort(d.efValidatedAt) : '—')
        },
        {
            key: 'startDate',
            header: translate('devList.colStartDate'),
            sortValue: (d) => d.startDate ?? '',
            render: (d) => (d.startDate ? formatDateShort(d.startDate) : '—')
        },
        {
            key: 'estimatedDeliveryDate',
            header: translate('devList.colEstimatedDelivery'),
            sortValue: (d) => d.estimatedDeliveryDate ?? '',
            render: (d) => (d.estimatedDeliveryDate ? formatDateShort(d.estimatedDeliveryDate) : '—')
        },
        {
            key: 'devEndDate',
            header: translate('devList.colDevEndDate'),
            sortValue: (d) => d.devEndDate ?? '',
            render: (d) => (d.devEndDate ? formatDateShort(d.devEndDate) : '—')
        }
    ];
}

export function buildDevelopmentListColumns(translate: TranslateFn): DataTableColumn<DevelopmentSummaryModel>[] {
    return [...buildIdentifierColumns(translate), ...buildContextColumns(translate), ...buildComplexityColumns(translate), ...buildDateColumns(translate)];
}
